import React, { type FC, useCallback, useContext, useEffect, useState, useRef } from 'react';
import { styled } from '@compiled/react';
import { useAnalyticsEvents, type UIAnalyticsEvent } from '@atlaskit/analytics-next';
import Button from '@atlaskit/button';
import DropdownMenu, { DropdownItemGroup, DropdownItem } from '@atlaskit/dropdown-menu';
import MoreIcon from '@atlaskit/icon/glyph/more';
import { Box, xcss } from '@atlaskit/primitives';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import log from '@atlassian/jira-common-util-logging/src/log';
import { fireErrorAnalytics } from '@atlassian/jira-errors-handling';
import { ff } from '@atlassian/jira-feature-flagging';
import { fg } from '@atlassian/jira-feature-gating';
import {
	EntryKeys,
	useFeedbackCollectorActions,
	ENTRYPOINT_ID_JIRA_PLATFORM,
} from '@atlassian/jira-feedback-collector';
import { useIntl } from '@atlassian/jira-intl';
import type { SearchResultPageData } from '@atlassian/jira-issue-navigator';
import { ErrorState } from '@atlassian/jira-issue-navigator-actions-common';
import { NinChangeboardingContext } from '@atlassian/jira-issue-navigator-changeboarding/src/controllers/enable-nin-changeboarding';
import { useGlobalScopeOptOutFlow } from '@atlassian/jira-issue-navigator-changeboarding/src/controllers/global-scope-enrolment-flow';
import normaliseUrl from '@atlassian/jira-platform-url-normalisation';
import { fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { useIsAnonymous } from '@atlassian/jira-tenant-context-controller';
import {
	useMeatballMenuItems,
	type MeatballMenuItem,
	type RequestPayload,
} from '../services/meatball-menu';
import {
	BULK_CHANGE,
	CLEAR_SORTS,
	CURRENT_PAGE,
	NIN_OPT_OUT_FEEDBACK,
	GIVE_FEEDBACK,
} from './constants';
import { DropdownItemLink } from './dropdown-item-link';
import messages from './messages';

export type MeatballMenuProps = {
	jql: string;
	filterId?: string;
	searchResultPageData: SearchResultPageData;
	additionalMenuItems?: React.ReactNode;
};

const FeedbackTitleDetails = () => {
	const { formatMessage } = useIntl();

	return (
		<Box xcss={feedbackTitleDetailsContainerStyles}>
			{/* eslint-disable-next-line @atlaskit/design-system/use-primitives-text */}
			<p>{formatMessage(messages.feedbackTitleDetails)}</p>
			<p>{formatMessage(messages.feedbackTitleDetailsContactSupport)}</p>
		</Box>
	);
};

export const getMeatballMenuItems = (
	data: ReturnType<typeof useMeatballMenuItems>['data'],
): MeatballMenuItem[] | undefined =>
	data &&
	data.flatMap((group) =>
		group.items.filter((item) => item.id !== CLEAR_SORTS && item.id !== CURRENT_PAGE),
	);

export const MeatballMenu: FC<MeatballMenuProps> = ({
	jql,
	filterId = '',
	searchResultPageData,
	additionalMenuItems,
}: MeatballMenuProps) => {
	const { data, loading, error, fetch, requestPayload } =
		useMeatballMenuItems(searchResultPageData);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { formatMessage } = useIntl();
	const isNinChangeboardingEnabled = useContext(NinChangeboardingContext);
	const isAnonymous = useIsAnonymous();
	const shouldShowOptOut = isNinChangeboardingEnabled && !isAnonymous;
	const { openFeedbackCollector } = useFeedbackCollectorActions();

	const meatballMenuItems = getMeatballMenuItems(data) || [];

	const [isOpen, setIsOpen] = useState(false);
	const lastUsedRequestPayload = useRef<RequestPayload>();
	const hasPreviousError = useRef(false);

	const fireMenuItemAnalytic = useCallback(
		(menuItemId: string) => {
			const analyticsEvent: UIAnalyticsEvent = createAnalyticsEvent({
				action: 'clicked',
			});
			fireUIAnalytics(analyticsEvent, 'dropdownItem clicked', 'meatballMenu', {
				dropdownItem: menuItemId,
			});
		},
		[createAnalyticsEvent],
	);

	const fireBulkEditFormAnalytic = useCallback(
		(menuItemId: string) => {
			const analyticsEvent: UIAnalyticsEvent = createAnalyticsEvent({
				action: 'submitted',
			});
			fireUIAnalytics(analyticsEvent, 'bulkEditForm submitted', 'meatballMenu', {
				dropdownItem: menuItemId,
			});
		},
		[createAnalyticsEvent],
	);

	const fireErrorAnalyticWhenFormIsNotInitialised = useCallback((menuItemId: string) => {
		const err = new Error(`Bulk edit form with id ${menuItemId} is not set`);
		fireErrorAnalytics({
			meta: {
				id: 'submitBulkEditForm',
				packageName: 'jiraIssueNavigatorActionMeatballMenu',
				teamName: 'empanada',
			},
			sendToPrivacyUnsafeSplunk: true,
			error: err,
		});
	}, []);

	const submitBulkEditForm = useCallback(
		(item: MeatballMenuItem) => {
			const form = document.getElementById(`export-bulk-items-form-${item.id}`);
			if (!(form instanceof HTMLFormElement)) {
				fireErrorAnalyticWhenFormIsNotInitialised(item.id);
				return;
			}
			fireBulkEditFormAnalytic(item.id);
			form.submit();
		},
		[fireErrorAnalyticWhenFormIsNotInitialised, fireBulkEditFormAnalytic],
	);

	const isBulkEdit = (item: MeatballMenuItem) =>
		item.id === BULK_CHANGE || item.id === CURRENT_PAGE;

	const onClickDropdownItem = useCallback(
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		(item: MeatballMenuItem) => (event: any) => {
			// Disable browser link behaviour
			event.preventDefault();

			if (isBulkEdit(item)) {
				submitBulkEditForm(item);
			} else if (typeof item.link === 'string' && item.link.length > 0) {
				// We need to use Javascript to open this in a new tab/window so that we can use Javascript to close it later
				window.open(item.link);
			}

			fireMenuItemAnalytic(item.id);
		},
		[fireMenuItemAnalytic, submitBulkEditForm],
	);

	let OptOutFragment = null;

	const getBulkEditFormFragment = (item: MeatballMenuItem) => (
		<BulkEditForm
			data-testid="issue-navigator-action-meatball-menu.ui.export-bulk-items-form"
			id={`export-bulk-items-form-${item.id}`}
			method="post"
			target="_blank"
			action={item.link}
		>
			<input
				data-testid="issue-navigator-action-meatball-menu.ui.export-bulk-items-jql"
				type="hidden"
				name="jql"
				value={jql}
			/>
			<input
				data-testid="issue-navigator-action-meatball-menu.ui.export-bulk-items-filter-id"
				type="hidden"
				name="filterId"
				value={filterId}
			/>
		</BulkEditForm>
	);

	/**
	 * In most cases, clicking the bulk edit option will be handled by submitBulkEditForm which will serialise the JQL
	 * as part of the form payload, thus avoiding any server restrictions on URI length. However, we still need to
	 * specify the JQL parameter in the anchor tag href so the correct issues are shown when right-clicking + opening in
	 * new tab. That means it's still possible to encounter a 414 error for long JQL queries via the right-click menu,
	 * but it's more important that we show users the correct issue results in this instance.
	 */
	const getItemLink = useCallback(
		(item: MeatballMenuItem) => {
			if (isBulkEdit(item) && jql && fg('empanada_bulk_edit_url_for_right_click')) {
				try {
					const bulkEditUrl = new URL(item.link, window.location.origin);
					const issueSearchParams = { jql };

					const newParams = new URLSearchParams([
						...Array.from(bulkEditUrl.searchParams.entries()),
						...Object.entries(issueSearchParams),
					]).toString();

					return `${bulkEditUrl.pathname}?${newParams}`;
				} catch (err: unknown) {
					// TODO Remove temporary logging after rollout of empanada_bulk_edit_url_for_right_click
					log.unsafeErrorWithCustomerData(
						'jiraIssueNavigatorActionMeatballMenu.getItemLink',
						`Encountered an error parsing bulk edit item URL: ${item.link}`,
						err instanceof Error ? err : undefined,
					);
				}
			}

			return item.link ?? '#';
		},
		[jql],
	);

	if (ff('nin.global-scope_aqec8')) {
		// eslint-disable-next-line react-hooks/rules-of-hooks
		const globalScopeOptOut = useGlobalScopeOptOutFlow();

		// eslint-disable-next-line react-hooks/rules-of-hooks
		const onOptOut = useCallback(async () => {
			fireMenuItemAnalytic(NIN_OPT_OUT_FEEDBACK);
			globalScopeOptOut();
		}, [fireMenuItemAnalytic, globalScopeOptOut]);

		OptOutFragment = (
			<>
				<FooterDivider />
				<DropdownItemGroup>
					<DropdownItem key={NIN_OPT_OUT_FEEDBACK} onClick={onOptOut}>
						{/* eslint-disable-next-line @atlaskit/design-system/use-primitives-text */}
						<span data-testid="issue-navigator-action-meatball-menu.ui.opt-out-feedback">
							{formatMessage(messages.actionsOptOutDisplayText)}
						</span>
					</DropdownItem>
				</DropdownItemGroup>
			</>
		);
	}

	useEffect(() => {
		const isMeatballMenuOpenedWithNewPayload =
			isOpen && JSON.stringify(requestPayload) !== JSON.stringify(lastUsedRequestPayload.current);

		if (!loading && !hasPreviousError.current && isMeatballMenuOpenedWithNewPayload) {
			// @ts-expect-error - TS2345 - Argument of type 'RequestPayload | undefined' is not assignable to parameter of type 'RequestPayload'.
			fetch(requestPayload); // should not be potentially undefined after isM71ColumnConfigEnabled cleanup
			lastUsedRequestPayload.current = requestPayload;
		}

		// If there was a fetch error, we clear the lastUsedRequestPayload
		if (error) {
			hasPreviousError.current = true;
			lastUsedRequestPayload.current = undefined;
		}
	}, [fetch, isOpen, loading, requestPayload, error]);

	return (
		<DropdownMenu
			isLoading={
				loading && JSON.stringify(requestPayload) === JSON.stringify(lastUsedRequestPayload.current)
			}
			onOpenChange={(nextState) => {
				if (nextState.isOpen) {
					hasPreviousError.current = false;
				}
				setIsOpen(nextState.isOpen);
			}}
			isOpen={isOpen}
			trigger={({ triggerRef, ...props }) => (
				<Button
					{...props}
					testId="issue-navigator-action-meatball-menu.ui.menu-trigger"
					spacing="default"
					isSelected={isOpen}
					iconBefore={
						<MoreIcon
							label={
								!ff('nin.global-scope_aqec8') || !shouldShowOptOut
									? formatMessage(messages.actionsButtonLabel)
									: formatMessage(messages.actionsButtonWithOptOutLabel)
							}
							size="medium"
						/>
					}
					ref={triggerRef}
				/>
			)}
			shouldFlip
			placement="bottom-end"
			spacing="compact"
			testId="issue-navigator-action-meatball-menu.ui.dropdown-menu"
		>
			{!error ? (
				<>
					<DropdownItemGroup>
						{additionalMenuItems}
						{meatballMenuItems &&
							meatballMenuItems.map((option) => (
								<DropdownItemLink
									key={option.id}
									menuItemId={option.id}
									onClick={onClickDropdownItem(option)}
									href={getItemLink(option)}
									target="_blank"
									styleClass={option.styleClass}
								>
									<span data-testid="issue-navigator-action-meatball-menu.ui.menu-item">
										{option.label}
									</span>

									{/* We need to send the jql and filterId as form data, because in NIN we are not storing these values on the server anymore.
                                We cannot send them as query params, because some of our clients have very long jql strings that surpass our current jira restrictions. */}
									{isBulkEdit(option) && getBulkEditFormFragment(option)}
								</DropdownItemLink>
							))}
						<DropdownItem
							key={GIVE_FEEDBACK}
							onClick={() =>
								openFeedbackCollector({
									entrypointId: ENTRYPOINT_ID_JIRA_PLATFORM,
									feedbackGroupId: 'issue-navigator',
									feedbackTitle: formatMessage(messages.feedbackTitle),
									feedbackTitleDetails: <FeedbackTitleDetails />,
									feedbackContext: [
										{
											id: EntryKeys.LOCATION,
											value: normaliseUrl(window?.location?.href ?? ''),
										},
										{
											id: EntryKeys.SCREEN_RESOLUTION,
											value: `${window?.screen?.width ?? 'unknown'} x ${window?.screen?.height ?? 'unknown'}`,
										},
										{
											id: EntryKeys.USER_AGENT,
											value: window?.navigator?.userAgent ?? 'unknown',
										},
										{
											id: 'spreadsheet-redesign',
											value: ff('jsv-one-issue-table-spreadsheet-ui_ygrnf').toString(),
										},
									],
									feedbackGroupLabels: {
										// Custom dropdown placeholder
										empty: {
											selectOptionLabel: formatMessage(messages.feedbackTypePlaceholder),
										},
										// Use default labels for other options
										bug: {},
										comment: {},
										suggestion: {},
										question: {},
									},
								})
							}
						>
							{/* eslint-disable-next-line @atlaskit/design-system/use-primitives-text */}
							<span data-testid="issue-navigator-action-meatball-menu.ui.give-feedback">
								{formatMessage(messages.actionsGiveFeedbackDisplayText)}
							</span>
						</DropdownItem>
					</DropdownItemGroup>
					{ff('nin.global-scope_aqec8') && shouldShowOptOut && OptOutFragment}
				</>
			) : (
				<ErrorState
					onRetry={() => {
						// @ts-expect-error - TS2345 - Argument of type 'RequestPayload | undefined' is not assignable to parameter of type 'RequestPayload'.
						fetch(requestPayload); // should not be potentially undefined after isM71ColumnConfigEnabled cleanup
						lastUsedRequestPayload.current = requestPayload;
					}}
					pageAction="meatballMenu"
				/>
			)}
		</DropdownMenu>
	);
};

export default MeatballMenu;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const BulkEditForm = styled.form({
	display: 'none',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const FooterDivider = styled.div({
	width: '100%',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	borderTop: `${token('color.border', colors.N40)} solid 2px`,
	marginBottom: token('space.050', '4px'),
	marginTop: token('space.050', '4px'),
});

const feedbackTitleDetailsContainerStyles = xcss({
	marginBottom: 'space.300',
});
